import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Ship, Car, Clock, Shield, Phone, Mail, CheckCircle2, MapPin, Users, Luggage,
  Plane, Building2, Palmtree, Baby, Star, ArrowRight, Navigation, ParkingCircle,
  ExternalLink, BadgeCheck, Headphones, SprayCan, ChevronDown, CalendarClock, Anchor
} from "lucide-react";
import ContactPopup from "../../components/ContactPopup";
import PageTransition from "../../components/PageTransition";

const CruiseTerminalPage = () => {
  // Fixed pricing for common routes
  const fixedPricing = [
    { route: "Brisbane CBD → BICT", price: "From $110", time: "25-35 mins" },
    { route: "Brisbane Airport → BICT", price: "From $85", time: "15-20 mins" },
    { route: "Gold Coast → BICT", price: "From $245", time: "70-90 mins" },
    { route: "Sunshine Coast → BICT", price: "From $295", time: "80-100 mins" },
  ];

  // Trust badges
  const trustBadges = [
    { icon: Headphones, label: "24/7 Support" },
    { icon: SprayCan, label: "COVID-Clean Vehicles" },
    { icon: BadgeCheck, label: "Licensed Chauffeurs" },
    { icon: Baby, label: "Free Child Seats" },
  ];

  // FAQ items
  const faqItems = [
    {
      question: "How long does it take to get from Brisbane Airport to the Cruise Terminal?",
      answer: "The transfer from Brisbane Airport (BNE) to the Brisbane International Cruise Terminal takes approximately 15-20 minutes, depending on traffic conditions. Our chauffeurs know the fastest routes to ensure you arrive on time for your cruise departure."
    },
    {
      question: "Is there public transport to the BICT?",
      answer: "No, there is currently no train or bus service to the Brisbane International Cruise Terminal at Luggage Point (Pinkenba). This makes private transfers the most reliable and stress-free option for cruise passengers, especially those with heavy luggage."
    },
    {
      question: "Do you provide child seats?",
      answer: "Yes, we offer complimentary age-appropriate child seats including rear-facing infant seats, forward-facing toddler seats, and booster seats. Simply let us know when booking and we'll have the appropriate restraint ready for your little ones."
    },
    {
      question: "What happens if my ship is delayed?",
      answer: "We track all cruise ship arrivals in real-time through the Port of Brisbane schedule. If your ship is delayed, we automatically adjust your pickup time at no extra charge. Your chauffeur will be waiting when you clear customs, no matter what time you disembark."
    },
    {
      question: "Can you accommodate large groups with lots of luggage?",
      answer: "Absolutely! We have a range of vehicles from executive sedans to Mercedes Sprinter vans that can accommodate up to 11 passengers with luggage. For cruise passengers, we recommend our Mercedes V-Class or Sprinter for extra luggage space."
    }
  ];

  const whyChooseUs = [
    {
      icon: ParkingCircle,
      title: "Avoid the Parking Hassle",
      description: "On-site parking must be pre-booked and can be expensive for long-duration cruises."
    },
    {
      icon: Clock,
      title: "Zero Wait Times",
      description: "Don't stand in long taxi or rideshare queues with 3,000 other passengers disembarking at once."
    },
    {
      icon: Luggage,
      title: "Luggage Assistance",
      description: "Our drivers handle your heavy suitcases from your door to the terminal drop-off."
    },
    {
      icon: Shield,
      title: "Fixed Pricing",
      description: "No surge pricing or \"hidden fees\"—just reliable, luxury transport."
    }
  ];

  const serviceAreas = [
    {
      destination: "Brisbane Airport (BNE)",
      time: "15–20 Mins",
      type: "Flight-to-Ship Connections",
      icon: Plane
    },
    {
      destination: "Brisbane CBD",
      time: "25–35 Mins",
      type: "Hotel & Residential Pickups",
      icon: Building2
    },
    {
      destination: "Gold Coast",
      time: "70–90 Mins",
      type: "Luxury Coastal Transfers",
      icon: Palmtree
    },
    {
      destination: "Sunshine Coast",
      time: "80–100 Mins",
      type: "Door-to-Door Excellence",
      icon: Palmtree
    }
  ];

  const differences = [
    {
      title: "Modern Luxury Fleet",
      description: "Choose from our range of late-model executive sedans, spacious SUVs, or premium Mercedes-Benz vans for larger groups and extra luggage."
    },
    {
      title: "Professional Chauffeurs",
      description: "Our drivers are local experts, fully accredited, and committed to punctuality. We track your ship's arrival to ensure we are waiting for you the moment you clear customs."
    },
    {
      title: "Family Friendly",
      description: "Traveling with kids? We provide complimentary age-appropriate child seats (forward-facing, rear-facing, or boosters) upon request."
    }
  ];

  // Schema.org data for Service
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Brisbane International Cruise Terminal Transfers",
    "alternateName": "BICT Transfers",
    "description": "Reliable private transfers to Brisbane International Cruise Terminal (BICT) in Pinkenba. Luxury sedans & vans for groups. Fixed rates from $85.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Limo Near Me",
      "telephone": "+61481437772",
      "email": "info@limonearme.com.au",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1 Cruise Terminal Drive",
        "addressLocality": "Pinkenba",
        "addressRegion": "QLD",
        "postalCode": "4008",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-27.3867",
        "longitude": "153.1178"
      }
    },
    "areaServed": [
      { "@type": "City", "name": "Brisbane" },
      { "@type": "City", "name": "Gold Coast" },
      { "@type": "City", "name": "Sunshine Coast" },
      { "@type": "Place", "name": "Luggage Point" },
      { "@type": "Place", "name": "Pinkenba" }
    ],
    "serviceType": "Cruise Terminal Transfer",
    "offers": {
      "@type": "Offer",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "85",
        "priceCurrency": "AUD",
        "description": "Starting price for Brisbane Airport to BICT transfer"
      }
    }
  };

  // FAQ Schema for rich results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Brisbane International Cruise Terminal Transfers | Private Limo & Van | BICT Pinkenba</title>
          <meta name="description" content="Reliable private transfers to Brisbane International Cruise Terminal (BICT) in Pinkenba. Luxury sedans & vans for groups. Fixed rates from $85. Book your chauffeur today!" />
          <meta name="keywords" content="Brisbane cruise terminal transfer, BICT transfer, Pinkenba cruise terminal, Luggage Point transfer, cruise ship transfer Brisbane, Brisbane cruise port transport, cruise terminal chauffeur, P&O cruise transfer, Carnival cruise Brisbane" />
          <link rel="canonical" href="https://www.limonearme.com.au/services/cruise-terminal" />
          <meta property="og:title" content="Brisbane International Cruise Terminal Transfers | Private Limo & Van" />
          <meta property="og:description" content="Reliable private transfers to BICT Pinkenba. Luxury sedans & vans. Fixed rates from $85. No public transport available - book your chauffeur today!" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.limonearme.com.au/services/cruise-terminal" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me - Brisbane Cruise Terminal Transfers" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/services/airport-transfers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Airport Transfers</Link>
                <Link to="/services/cruise-terminal" className="text-sm text-foreground font-medium">Cruise Terminal</Link>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-blue-50 to-background">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Ship className="h-4 w-4" />
                  No Public Transport to BICT
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                  Brisbane International Cruise Terminal (BICT) Transfers
                </h1>
                <p className="text-lg text-muted-foreground mb-4">
                  <strong>Luxury Private Chauffeur Services to & from Pinkenba / Luggage Point</strong>
                </p>
                <p className="text-muted-foreground mb-6">
                  Start your holiday the moment you leave your front door. <strong>Limo Near Me</strong> provides premium, door-to-door private transfers for the Brisbane International Cruise Terminal. Whether you're heading off on a South Pacific escape or returning home from a world cruise, our professional chauffeurs ensure your journey is as relaxing as the cruise itself.
                </p>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {trustBadges.map((badge, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white border border-black/10 rounded-full px-4 py-2 text-sm">
                      <badge.icon className="h-4 w-4 text-primary" />
                      <span className="font-medium">{badge.label}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/book">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Ship className="h-5 w-5 mr-2" />
                      Book Now
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Get a Quote
                    </Button>
                  </Link>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1548574505-5e239809ee19?w=600&h=400&fit=crop" 
                  alt="Luxury Mercedes Van transfer at Brisbane International Cruise Terminal BICT Pinkenba" 
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">BICT Pinkenba</p>
                      <p className="text-sm text-muted-foreground">Luggage Point, 30 mins from CBD</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Booking Urgency Banner */}
        <section className="py-4 bg-amber-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
              <CalendarClock className="h-6 w-6 text-amber-600" />
              <p className="text-amber-800">
                <strong>Booking for the Quantum of the Seas, Majestic Princess, or Pacific Explorer?</strong>{" "}
                We recommend booking at least 48 hours in advance for cruise transfers.
              </p>
              <Link to="/book">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700">Book Now</Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Fixed Pricing Table */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Fixed Transfer Pricing</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                No surge pricing. No hidden fees. Know your cost upfront.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {fixedPricing.map((item, index) => (
                <motion.div
                  key={item.route}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 text-center border-2 border-transparent hover:border-primary transition-colors"
                >
                  <p className="font-semibold text-foreground mb-2">{item.route}</p>
                  <Link to="/book"><Button size="sm" variant="outline" className="rounded-full mb-2">Get Price</Button></Link>
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                    <Clock className="h-4 w-4" /> {item.time}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Prices shown for Executive Sedan. SUVs, vans, and minibuses available at additional cost.
              </p>
              <Link to="/book">
                <Button size="lg">
                  Get Instant Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Why Choose a Private Transfer for BICT?</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                The Brisbane International Cruise Terminal is located at <strong>Luggage Point (Pinkenba)</strong>, roughly 30 minutes from the CBD and 15 minutes from Brisbane Airport. Unlike other major terminals, <strong className="text-red-600">there is currently no public train or bus service</strong> to the BICT.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Our Service Areas</h2>
              <p className="text-muted-foreground">
                Seamless connections between the cruise terminal and all major Southeast Queensland hubs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => (
                <motion.div
                  key={area.destination}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 border border-black/5 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <area.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{area.destination}</h3>
                  <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                    <Clock className="h-4 w-4" />
                    {area.time}
                  </div>
                  <p className="text-sm text-muted-foreground">{area.type}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Landmarks & Local Area */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Terminal Location & Nearby Attractions</h2>
                
                <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Brisbane International Cruise Terminal</h3>
                      <p className="text-muted-foreground">1 Cruise Terminal Drive, Pinkenba QLD 4008</p>
                      <p className="text-sm text-muted-foreground mt-1">Also known as: Luggage Point Cruise Terminal</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold">Nearby Landmarks:</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <strong>Eat Street Northshore</strong> – 10 mins (popular dining destination)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <strong>Portside Wharf</strong> – 15 mins (restaurants & entertainment)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <strong>Hamilton</strong> – 15 mins (boutique shopping & cafes)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <strong>Brisbane Airport</strong> – 15-20 mins
                    </li>
                  </ul>
                </div>

                {/* Ship Tracker Link */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <Anchor className="h-6 w-6" />
                    <h4 className="font-bold text-lg">Is Your Ship On Time?</h4>
                  </div>
                  <p className="text-blue-100 mb-4 text-sm">
                    Check the official Port of Brisbane shipping schedule for real-time arrival and departure information.
                  </p>
                  <a 
                    href="https://www.portbris.com.au/cruise/cruise-schedule/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    View Ship Schedule
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
              
              {/* Google Map Embed */}
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3540.0123456789!2d153.1178!3d-27.3867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b915a1d5c0a0001%3A0x502a35af3de9e80!2sBrisbane%20International%20Cruise%20Terminal!5e0!3m2!1sen!2sau!4v1234567890"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Brisbane International Cruise Terminal Location Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* The Difference Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">The Limo Near Me Difference</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {differences.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Star className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Terminal Info Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Meeting Points & Drop-off</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Navigation className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Drop-off (Embarkation)</h4>
                      <p className="text-sm text-muted-foreground">We deliver you directly to the 2-minute Passenger Set Down area at the front of the terminal building. Your chauffeur will assist with all luggage.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Pick-up (Disembarkation)</h4>
                      <p className="text-sm text-muted-foreground">For disembarking passengers, your chauffeur will coordinate a meeting point or wait in the designated Pre-Booked Hire Car area for a seamless exit.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Ship className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Ship Tracking</h4>
                      <p className="text-sm text-muted-foreground">We monitor your ship's arrival time. If there are delays, we automatically adjust your pickup—no need to call or stress.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1599640842225-85d111c60e6b?w=600&h=450&fit=crop" 
                  alt="Professional chauffeur service for Brisbane cruise terminal passengers" 
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Everything you need to know about BICT cruise terminal transfers
              </p>
            </div>

            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6"
                >
                  <h3 className="font-semibold text-lg mb-3 flex items-start gap-3">
                    <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                      {index + 1}
                    </span>
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground ml-11">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Ship className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Book Your Cruise Transfer Today</h2>
            <p className="text-primary-foreground/80 mb-8 text-lg">
              Ready to travel in style? Don't leave your transport to chance on embarkation day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto px-8">
                  Get a Quote
                </Button>
              </Link>
              <Link to="/book">
                <Button size="lg" className="w-full sm:w-auto px-8 bg-white text-primary hover:bg-gray-100">
                  Book Now
                </Button>
              </Link>
            </div>
            <p className="mt-8 text-sm text-primary-foreground/70">
              Have a large group or special requirements? Call us at{" "}
              <a href="tel:+61481437772" className="underline font-semibold">+61 481 437 772</a> or email{" "}
              <a href="mailto:info@limonearme.com.au" className="underline">info@limonearme.com.au</a>
            </p>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default CruiseTerminalPage;
